import { Link } from 'react-router-dom'

import { cn } from '@/lib/cn'
import { formatDateTerse, formatDaysUntil } from '@/lib/dates'
import { taskUrgency, useDeleteTask, useToggleTask, type TaskWithRelations } from './queries'

const URGENCY_CLASS = {
  overdue: 'text-clay',
  today: 'text-clay',
  soon: 'text-amber',
  later: 'text-taupe',
  someday: 'text-taupe',
} as const

/**
 * One task. A native checkbox restyled to the square hairline, so keyboard and
 * screen-reader behaviour comes from the browser rather than from us.
 */
type Bucket = 'today' | 'soon' | 'ideas'
const BUCKET_LABEL: Record<Bucket, string> = { today: 'Today', soon: 'Soon', ideas: 'Ideas' }

export function TaskRow({
  task,
  compact = false,
  onMove,
}: {
  task: TaskWithRelations
  compact?: boolean
  /** Shown on the Today / Soon / Ideas board: move the card to another list. */
  onMove?: (bucket: Bucket) => void
}) {
  const toggle = useToggleTask()
  const remove = useDeleteTask()

  const done = Boolean(task.completed_at)
  const urgency = taskUrgency(task)

  return (
    <li className="group flex items-start gap-2.5 border-b py-2">
      <input
        type="checkbox"
        checked={done}
        aria-label={done ? `Mark "${task.title}" as not done` : `Mark "${task.title}" as done`}
        onChange={(event) => toggle.mutate({ id: task.id, done: event.target.checked })}
        className={
          'mt-[3px] size-[15px] shrink-0 appearance-none rounded-none border border-ink bg-transparent ' +
          'check checked:bg-ink transition-colors duration-[var(--dur-fast)] ' +
          'checked:bg-center checked:bg-no-repeat'
        }
      />

      <div className="min-w-0 flex-1">
        <p className={cn('text-[length:var(--text-sm)] leading-snug', done && 'text-taupe line-through')}>{task.title}</p>

        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[length:var(--text-2xs)]">
          {task.due_date ? (
            <span className={done ? 'text-taupe' : URGENCY_CLASS[urgency]}>
              {formatDateTerse(task.due_date)} · {formatDaysUntil(task.due_date)}
            </span>
          ) : (
            <span className="text-taupe">No date</span>
          )}

          {!compact && task.job ? (
            <Link
              to={`/weddings/${task.job.id}`}
              className="text-taupe underline decoration-[var(--color-rule-strong)] underline-offset-4 hover:text-ink"
            >
              Wedding {task.job.job_number}
            </Link>
          ) : null}

          {!compact && task.assignee ? <span className="text-taupe">{task.assignee.full_name}</span> : null}
        </div>
      </div>

      {onMove ? (
        <span className="flex shrink-0 gap-2 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          {(Object.keys(BUCKET_LABEL) as Bucket[])
            .filter((bucket) => bucket !== task.bucket)
            .map((bucket) => (
              <button key={bucket} type="button" className="label hover:text-ink" onClick={() => onMove(bucket)}>
                {BUCKET_LABEL[bucket]}
              </button>
            ))}
        </span>
      ) : null}

      <button
        type="button"
        onClick={() => remove.mutate(task.id)}
        aria-label={`Delete "${task.title}"`}
        className="label shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 hover:text-clay"
      >
        Delete
      </button>
    </li>
  )
}
